import React, { useState, useEffect } from "react";
import "./BackupModal.css";
import {
  listSchedules,
  loadSchedule,
  saveSchedule,
  deleteSchedule,
  ScheduleMetadata,
  serializeCategories,
} from "../services/api";
import { Category } from "../models/Category";
import { Project } from "../models/Project";
import { formatDateFrench } from "../utils/dateFormatting";
import { ClientProject } from "../types";
import {
  getConfiguredMonthlyCapacity,
  setConfiguredMonthlyCapacity,
} from "../config/availableEffort";
import { useAuth } from "../context/AuthContext";

interface BackupModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentCategories: Category[];
  currentClientProjects: ClientProject[];
  onLoadSchedule: (categories: Category[], clientProjects: ClientProject[]) => void;
}

type TabType = "save" | "load";

const BackupModal: React.FC<BackupModalProps> = ({
  isOpen,
  onClose,
  currentCategories,
  currentClientProjects,
  onLoadSchedule,
}) => {
  const { canEdit } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>(
    canEdit ? "save" : "load",
  );
  const [schedules, setSchedules] = useState<ScheduleMetadata[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saveId, setSaveId] = useState("");
  const [selectedSchedule, setSelectedSchedule] = useState<string | null>(null);

  // Load schedules when modal opens or tab changes to 'load'
  useEffect(() => {
    if (isOpen && activeTab === "load") {
      loadSchedulesList();
    }
  }, [isOpen, activeTab]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setSuccess(null);
      setSaveId("");
      setSelectedSchedule(null);
      setActiveTab(canEdit ? "save" : "load");
    }
  }, [isOpen, canEdit]);

  const loadSchedulesList = async () => {
    setLoading(true);
    setError(null);
    try {
      const scheduleList = await listSchedules();
      setSchedules(scheduleList);
    } catch (err: any) {
      setError(err.message || "Failed to load schedules list");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!saveId.trim()) {
      setError("Please enter a schedule name");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const serializedCategories = serializeCategories(currentCategories);
      await saveSchedule(
        saveId.trim(),
        serializedCategories,
        getConfiguredMonthlyCapacity(),
        currentClientProjects,
      );
      setSuccess(`Schedule saved successfully as "${saveId}"`);
      setSaveId("");

      // Refresh list if on load tab
      if (activeTab === "load") {
        await loadSchedulesList();
      }
    } catch (err: any) {
      setError(err.message || "Failed to save schedule");
    } finally {
      setLoading(false);
    }
  };

  const handleLoad = async () => {
    if (!selectedSchedule) {
      setError("Please select a schedule to load");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const data = await loadSchedule(selectedSchedule);

      // Reconstruct Category and Project objects
      const reconstructedCategories = reconstructCategories(data.categories);

      if (data.capacity) {
        setConfiguredMonthlyCapacity(data.capacity);
      }

      onLoadSchedule(reconstructedCategories, data.clientProjects || []);
      setSuccess(`Schedule "${selectedSchedule}" loaded successfully`);

      // Close modal after successful load
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || "Failed to load schedule");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(`Are you sure you want to delete schedule "${id}"?`)) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await deleteSchedule(id);
      setSuccess(`Schedule "${id}" deleted successfully`);
      await loadSchedulesList();

      if (selectedSchedule === id) {
        setSelectedSchedule(null);
      }
    } catch (err: any) {
      setError(err.message || "Failed to delete schedule");
    } finally {
      setLoading(false);
    }
  };

  const reconstructCategories = (data: any[]): Category[] => {
    return data.map((catData) => {
      // Reconstruct projects
      const projects = (catData.projects || []).map((p: any) =>
        Project.build(p),
      );

      // Recursively reconstruct subcategories (already hierarchical in JSON)
      const subcategories = reconstructCategories(catData.subcategories || []);

      return new Category({
        id: catData.id,
        name: catData.name,
        color: catData.color,
        projects,
        subcategories,
        parentId: catData.parentId,
        level: catData.level,
      });
    });
  };

  if (!isOpen) return null;

  return (
    <div className="backup-modal-overlay" onClick={onClose}>
      <div className="backup-modal" onClick={(e) => e.stopPropagation()}>
        <div className="backup-modal-header">
          <h2>💾 Backup & Restore</h2>
          <button className="backup-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="backup-tabs">
          {canEdit && (
            <button
              className={`backup-tab ${activeTab === "save" ? "active" : ""}`}
              onClick={() => setActiveTab("save")}
            >
              💾 Save Schedule
            </button>
          )}
          <button
            className={`backup-tab ${activeTab === "load" ? "active" : ""}`}
            onClick={() => setActiveTab("load")}
          >
            📂 Load Schedule
          </button>
        </div>

        <div className="backup-modal-content">
          {error && (
            <div className="backup-message error">
              <span className="backup-message-icon">❌</span>
              {error}
            </div>
          )}

          {success && (
            <div className="backup-message success">
              <span className="backup-message-icon">✅</span>
              {success}
            </div>
          )}

          {canEdit && activeTab === "save" && (
            <div className="backup-save-section">
              <p className="backup-description">
                Save your current schedule to the backend server. You can
                restore it later.
              </p>

              <div className="backup-form-group">
                <label htmlFor="save-id">Schedule Name:</label>
                <input
                  type="text"
                  id="save-id"
                  value={saveId}
                  onChange={(e) => setSaveId(e.target.value)}
                  placeholder="e.g., my-schedule-2024"
                  disabled={loading}
                  onKeyPress={(e) => e.key === "Enter" && handleSave()}
                />
                <small>Use a unique name to identify this schedule</small>
              </div>

              <button
                className="backup-action-button save-button"
                onClick={handleSave}
                disabled={loading || !saveId.trim()}
              >
                {loading ? "💾 Saving..." : "💾 Save to Server"}
              </button>
            </div>
          )}

          {activeTab === "load" && (
            <div className="backup-load-section">
              <p className="backup-description">
                Select a previously saved schedule to restore.
              </p>

              {loading && schedules.length === 0 && (
                <div className="backup-loading">Loading schedules...</div>
              )}

              {!loading && schedules.length === 0 && (
                <div className="backup-empty">
                  <p>No saved schedules found.</p>
                  <p>
                    Save your current schedule first using the "Save Schedule"
                    tab.
                  </p>
                </div>
              )}

              {schedules.length > 0 && (
                <div className="backup-schedule-list">
                  {schedules.map((schedule) => (
                    <div
                      key={schedule.id}
                      className={`backup-schedule-item ${
                        selectedSchedule === schedule.id ? "selected" : ""
                      }`}
                      onClick={() => setSelectedSchedule(schedule.id)}
                    >
                      <div className="backup-schedule-info">
                        <h4>{schedule.id}</h4>
                        <div className="backup-schedule-meta">
                          <span>
                            📅 {formatDateFrench(new Date(schedule.savedAt))}
                          </span>
                          <span>📝 {schedule.projectCount} projects</span>
                        </div>
                      </div>
                      {canEdit && (
                        <button
                          className="backup-delete-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(schedule.id);
                          }}
                          disabled={loading}
                          title="Delete this schedule"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {schedules.length > 0 && (
                <button
                  className="backup-action-button load-button"
                  onClick={handleLoad}
                  disabled={loading || !selectedSchedule}
                >
                  {loading ? "📂 Loading..." : "📂 Load Selected Schedule"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BackupModal;
